/**
 * 客户端同步（阶段 4：本地优先 + 事件同步，ADR-004/013）。
 *
 * 职责：
 *   - flushOutbox()：把 syncEvents 出站队列（幂等键 = 事件 id）批量 POST 到
 *     sync-push Edge Function；成功后标记 synced，冲突记录留档本地；
 *   - pullAndApply()：按 meta.lastSyncCursor 增量拉取，权威值应用到本地 typed 表，
 *     双版本 extras 存入通用 syncEntities 表（Dexie v2），冲突留档；
 *   - syncNow()：组合调度（未配置 Supabase 时如实返回 mode="local"，不伪造同步）。
 */

import { db } from "../../db/db";
import { applyPullDelta, sortPending } from "@jilian/sync";
import type { PullDeltaItem, SyncEventEnvelope, SyncEntityPayload, SyncEntityType } from "@jilian/sync";
import type { SupabaseClient } from "@supabase/supabase-js";
import { createAuthProvider, type AuthProvider } from "./auth";
import { addLocalConflicts, getMetaNumber, setMeta } from "../../db/repo";

const AUTH_PROVIDER: AuthProvider = createAuthProvider();

export interface SyncSummary {
  mode: "local" | "supabase";
  uploaded: number;
  applied: number;
  conflicts: number;
  message: string;
}

function supabaseOf(): SupabaseClient | null {
  const p = AUTH_PROVIDER as { getClient?: () => SupabaseClient };
  return typeof p.getClient === "function" ? p.getClient() : null;
}

/** 本地 syncEvents 行 → 事件信封（幂等键 = 事件 id，重传复用同一对象语义）。 */
function toEnvelope(row: {
  id: string;
  userId: string;
  deviceId: string;
  entityType: string;
  entityId: string;
  operation: string;
  entityVersion: number;
  payload: unknown;
  clientCreatedAt: string;
}): SyncEventEnvelope {
  return {
    id: row.id,
    userId: row.userId,
    deviceId: row.deviceId,
    entityType: row.entityType as SyncEntityType,
    entityId: row.entityId,
    operation: row.operation === "delete" ? "delete" : "upsert",
    entityVersion: row.entityVersion,
    payload: row.payload as SyncEntityPayload,
    clientCreatedAt: row.clientCreatedAt,
  };
}

interface PushResponse {
  applied: number;
  duplicates: number;
  conflicts: Array<{ eventId: string; entityType: string; entityId: string; resolution: string; reasonCodes: string[]; createdAt: string }>;
  cursor: number;
}

export async function flushOutbox(): Promise<{ uploaded: number; conflicts: number; cursor: number }> {
  const supabase = supabaseOf();
  if (!supabase) throw new Error("supabase_unavailable");

  const pending = await db.syncEvents.where("syncStatus").equals("pending").toArray();
  if (pending.length === 0) return { uploaded: 0, conflicts: 0, cursor: 0 };
  const envelopes = sortPending(pending.map(toEnvelope));

  const { data, error } = await supabase.functions.invoke<PushResponse>("sync-push", { body: { events: envelopes } });
  if (error) throw new Error(`sync_push_failed: ${error.message}`);

  // 成功上传 → 标记 synced；服务端判定冲突的事件标记 conflict 并留档
  const conflictIds = new Set((data?.conflicts ?? []).map((c) => c.eventId));
  await db.transaction("rw", db.syncEvents, async () => {
    for (const row of pending) {
      await db.syncEvents.update(row.id, { syncStatus: conflictIds.has(row.id) ? "conflict" : "synced" });
    }
  });

  if (data && data.conflicts.length > 0) {
    await addLocalConflicts(
      data.conflicts.map((c) => ({
        entityType: c.entityType,
        entityId: c.entityId,
        resolution: c.resolution,
        reasonCodes: c.reasonCodes ?? [],
        createdAt: c.createdAt ?? new Date().toISOString(),
        acknowledged: false,
      })),
    );
  }
  return { uploaded: data?.applied ?? 0, conflicts: data?.conflicts.length ?? 0, cursor: data?.cursor ?? 0 };
}

/** 本地 typed 表映射（事件类型 → Dexie 表名）。 */
const TYPED_TABLES: Record<string, { table: "sessions" | "exerciseItems" | "sets" | "meals" | "measurements" | "dailyIntent"; ids: Set<string> }> = {
  workout_session: { table: "sessions", ids: new Set() },
  workout_exercise: { table: "exerciseItems", ids: new Set() },
  workout_set: { table: "sets", ids: new Set() },
  meal_entry: { table: "meals", ids: new Set() },
  body_measurement: { table: "measurements", ids: new Set() },
  daily_intent: { table: "dailyIntent", ids: new Set() },
};

type TableLike = { bulkGet: (ids: string[]) => Promise<unknown[]>; put: (v: unknown) => Promise<unknown> };

export async function pullAndApply(): Promise<{ applied: number; conflicts: number; cursor: number }> {
  const supabase = supabaseOf();
  if (!supabase) throw new Error("supabase_unavailable");

  const cursor = await getMetaNumber("lastSyncCursor");
  // functions-js 无 query 选项：游标拼进函数名 URL（服务端经 request.url.searchParams 读取）
  const { data, error } = await supabase.functions.invoke<{ items: PullDeltaItem[]; cursor: number; hasMore: boolean }>(
    `sync-pull?cursor=${encodeURIComponent(cursor)}`,
    { method: "GET" },
  );
  if (error) throw new Error(`sync_pull_failed: ${error.message}`);
  const items = data?.items ?? [];
  if (items.length === 0) return { applied: 0, conflicts: 0, cursor };

  // 1) 从本地 typed 表载入受影响实体（权威值合并的输入）
  for (const item of items) {
    const bucket = TYPED_TABLES[item.event.entityType];
    if (bucket) bucket.ids.add(item.event.entityId);
  }
  const map = new Map<string, SyncEntityPayload>();
  for (const [type, bucket] of Object.entries(TYPED_TABLES)) {
    if (bucket.ids.size === 0) continue;
    const rows = await (db[bucket.table] as unknown as TableLike).bulkGet([...bucket.ids]);
    for (const row of rows) {
      if (row) map.set(`${type}:${(row as { id: string }).id}`, row as SyncEntityPayload);
    }
  }

  // 2) 应用权威值 / extras / 冲突（纯逻辑与服务端同源，packages/sync 单测覆盖）
  const result = applyPullDelta(map, items);

  // 3) 回写本地：typed 表主版本；extras（双版本）与未建 typed 表的实体 →
  //    syncEntities 通用表（阶段 4 起按类型逐步接入，不静默丢弃）
  const nowIso = () => new Date().toISOString();
  for (const item of items) {
    const baseKey = `${item.event.entityType}:${item.event.entityId}`;
    const bucket = TYPED_TABLES[item.event.entityType];
    if (item.authority == null) {
      if (bucket) {
        await (db[bucket.table] as unknown as { delete: (id: string) => Promise<void> }).delete(item.event.entityId);
      }
      continue;
    }
    if (bucket) {
      await (db[bucket.table] as unknown as TableLike).put(item.authority);
    } else {
      await db.syncEntities.put({
        key: baseKey,
        entityType: item.event.entityType,
        entityId: item.event.entityId,
        version: (item.authority as { version?: number }).version ?? 1,
        payload: item.authority,
        updatedAt: nowIso(),
      });
    }
    for (const extra of item.extras) {
      const v = (extra as { version?: number }).version ?? 1;
      await db.syncEntities.put({
        key: `${baseKey}#v${v}`,
        entityType: item.event.entityType,
        entityId: item.event.entityId,
        version: v,
        payload: extra,
        updatedAt: nowIso(),
      });
    }
  }

  if (result.conflicts.length > 0) {
    await addLocalConflicts(
      result.conflicts.map((c) => ({
        entityType: c.entityType,
        entityId: c.entityId,
        resolution: c.resolution,
        reasonCodes: c.reasonCodes,
        createdAt: c.createdAt,
        acknowledged: false,
      })),
    );
  }
  if (data) await setMeta("lastSyncCursor", data.cursor);
  return { applied: result.applied, conflicts: result.conflicts.length, cursor: data?.cursor ?? cursor };
}

/** 组合调度：出站上传 + 增量拉取（本地模式如实返回，不伪造）。 */
export async function syncNow(): Promise<SyncSummary> {
  if (AUTH_PROVIDER.kind === "local") {
    const pending = await db.syncEvents.where("syncStatus").equals("pending").count();
    return {
      mode: "local",
      uploaded: 0,
      applied: 0,
      conflicts: 0,
      message: `本地模式：未配置账户服务，${pending} 条记录保留在本设备（配置 VITE_SUPABASE_URL 并登录后自动补同步）。`,
    };
  }
  const session = await AUTH_PROVIDER.getSession();
  if (!session) {
    return { mode: "supabase", uploaded: 0, applied: 0, conflicts: 0, message: "尚未登录：请先在「我的」页登录后同步。" };
  }
  const up = await flushOutbox();
  const pull = await pullAndApply();
  const conflicts = up.conflicts + pull.conflicts;
  return {
    mode: "supabase",
    uploaded: up.uploaded,
    applied: pull.applied,
    conflicts,
    message: `已上传 ${up.uploaded} 条、应用 ${pull.applied} 条${conflicts > 0 ? `，冲突 ${conflicts} 条（已保留双版本）` : ""}。`,
  };
}

export { AUTH_PROVIDER };